import { z } from 'zod';

export const FUNNEL_TYPES = [
  'direct_to_pdp',
  'landing_page_to_pdp',
  'landing_page_to_offer',
  'direct_to_offer',
  'collection_to_pdp',
  'quiz',
  'subscription',
  'free_shipping',
  'post_purchase_upsell',
] as const;

export const LEGACY_FUNNEL_TYPE_ALIASES = {
  presell_to_pdp: 'landing_page_to_pdp',
  presell_to_offer: 'landing_page_to_offer',
} as const;

export const STEP_TYPES = [
  'product_page',
  'offer_page',
  'collection',
  'landing_page',
  'cart',
  'checkout',
  'thank_you',
  'upsell',
  'downsell',
] as const;

export const LEGACY_STEP_TYPES = [
  'presell',
  'quiz_entry',
  'quiz_step',
  'quiz_result',
] as const;

export const STEP_TRANSITION_EVENTS = [
  'page_view',
  'accept',
  'decline',
  'timeout',
  'cart_updated',
  'order_created',
  // owner 2026-07-15: post-purchase order changes (upsell accepted, items
  // added/removed). Wire + behavioral + builder coverage tracked in WP-0..WP-5.
  'order_updated',
] as const;

export const EXPERIENCE_TYPES = [
  'page',
  'modal',
  'popup',
  'banner',
  'toast',
  'none',
] as const;

export const PLACEMENTS = [
  'post_purchase',
  'product_page',
  'collection',
  'landing_page',
  'offer_page',
  'cart',
  'checkout',
  'thank_you',
] as const;

export const STEP_TYPE_EXPERIENCE_MAP: Record<
  (typeof STEP_TYPES)[number],
  readonly (typeof EXPERIENCE_TYPES)[number][]
> = {
  product_page: ['page', 'popup', 'banner', 'modal', 'toast'],
  landing_page: ['page'],
  collection: ['page', 'banner', 'toast'],
  offer_page: ['page'],
  cart: ['page', 'banner', 'toast', 'none'],
  checkout: ['page', 'banner', 'toast', 'none'],
  thank_you: ['page'],
  upsell: ['page', 'modal', 'popup'],
  downsell: ['page', 'modal', 'popup'],
};

// absent ⇒ unlimited; flat per-funnel; canonical SoT for funnels-go + funnels-mcp + backoffice.
export const STEP_TYPE_MAX_COUNTS: Partial<
  Record<(typeof STEP_TYPES)[number], number>
> = {
  landing_page: 1,
  cart: 1,
  checkout: 1,
  thank_you: 1,
  upsell: 5,
  downsell: 4,
};

export const FUNNEL_TYPE_ENTRY = {
  direct_to_pdp: 'product_page',
  direct_to_offer: 'offer_page',
  landing_page_to_pdp: 'landing_page',
  landing_page_to_offer: 'landing_page',
  collection_to_pdp: 'collection',
  quiz: 'landing_page',
  subscription: 'landing_page',
  free_shipping: 'landing_page',
  post_purchase_upsell: 'checkout',
} as const satisfies Record<
  (typeof FUNNEL_TYPES)[number],
  (typeof STEP_TYPES)[number]
>;

export const FRONT_STEP_TYPES = [
  'product_page',
  'offer_page',
  'landing_page',
  'collection',
] as const satisfies readonly (typeof STEP_TYPES)[number][];

export const TAIL_STEP_TYPES = [
  'upsell',
  'downsell',
] as const satisfies readonly (typeof STEP_TYPES)[number][];

export const TRAFFIC_TYPES = ['acq', 'str', 'ret'] as const;

export const FLOW_VARIANTS = ['main', 'ab', 'qa'] as const;

export const TRIGGER_LIFECYCLES = ['lifetime', 'session', 'page'] as const;

export const FREQUENCY_CAPS = [
  'once_per_session',
  'once_per_day',
  'once_ever',
  'always',
  'unlimited',
] as const;

export const LAYOUT_MODES = ['card', 'medium', 'full_width'] as const;

export const DISCOUNT_TYPES = [
  'percentage',
  'fixed',
  'free_shipping',
] as const;

export const OFFER_MODES = ['funnel', 'step'] as const;

export const FUNNEL_STATUSES = [
  'active',
  'draft',
  'archived',
  'paused',
  'scheduled',
] as const;

export const MATCH_MODES = ['any', 'all'] as const;

export const OFFER_SET_SELECTION_MODES = ['random', 'weighted'] as const;

export const TEMPLATE_CATEGORIES = [
  'offer_page',
  'landing_page',
  'product_page',
  'popup',
  'free_gift',
  'leads_popup',
  'free_gift_popup',
  'offer_popup',
] as const;

export const TEMPLATE_STATUSES = [
  'draft',
  'published',
  'archived',
] as const;

export const TRIGGER_TYPES = [
  'current_url',
  'landing_url',
  'landing_page_url',
  'url_contains',
  'url_parameter',
  'cart_total',
  'cart_item_count',
  'cart_contains_product',
  'cart_contains_collection',
  'cart_contains_tag',
  'device_type',
  'browser_language',
  'country',
  'customer_tag',
  'time_on_page',
  'scroll_depth',
  'exit_intent',
  'order_contains_product',
  'order_total',
  'purchase_history',
  'previous_funnel',
  'session_count',
  'day_of_week',
  'time_of_day',
  'date_range',
] as const;

export const OPERATOR_TYPES = [
  'equals',
  'not_equals',
  'greater_than',
  'less_than',
  'greater_than_or_equals',
  'less_than_or_equals',
  'contains',
  'not_contains',
  'in',
  'not_in',
  'starts_with',
  'ends_with',
  'between',
] as const;

export const LOGIC_OPERATORS = ['AND', 'OR'] as const;

export const ACTION_TYPES = [
  'go_to_url',
  'show_leads_popup',
  'show_free_gift_popup',
  'display_offer',
  'connect_to_system',
  'dispatch_event',
  'save_to_storage',
  // WP-3: commerce action. Mutates the cart only through the server-validated
  // BFF cart path (never a client-side price/quantity write). apply_discount is
  // intentionally NOT here yet — the BFF apply-coupon proxy leg does not exist.
  'add_to_cart',
] as const;

export const BLOCK_TYPES = [
  'hero-banner',
  'product-showcase',
  'testimonial-section',
  'feature-grid',
  'pricing-table',
  'faq-accordion',
  'newsletter-signup',
  'social-proof-bar',
  'spin-the-wheel',
  'image-text-block',
  'cta-button-group',
  'countdown-timer',
  'progress-bar',
  'video-embed',
  'icon-list',
  'stats-counter',
  'comparison-table',
  'badge-ribbon',
  'divider',
  'heading',
  'paragraph',
  'image',
  'button',
  'spacer',
  'container',
  'card',
  'input-field',
  'checkbox',
  'select',
] as const;

export const COMPONENT_TIERS = {
  ready_to_use: [
    'hero-banner',
    'product-showcase',
    'testimonial-section',
    'feature-grid',
    'pricing-table',
    'faq-accordion',
    'newsletter-signup',
    'social-proof-bar',
    'spin-the-wheel',
  ],
  quick_sections: [
    'image-text-block',
    'cta-button-group',
    'countdown-timer',
    'progress-bar',
    'video-embed',
    'icon-list',
    'stats-counter',
    'comparison-table',
    'badge-ribbon',
    'divider',
  ],
  building_blocks: [
    'heading',
    'paragraph',
    'image',
    'button',
    'spacer',
    'container',
    'card',
    'input-field',
    'checkbox',
    'select',
  ],
} as const;

export const ActionTypeEnum = z.enum(ACTION_TYPES);
export const BlockTypeEnum = z.enum(BLOCK_TYPES);
export const DiscountTypeEnum = z.enum(DISCOUNT_TYPES);
export const ExperienceTypeEnum = z.enum(EXPERIENCE_TYPES);
export const FlowVariantEnum = z.enum(FLOW_VARIANTS);
export const FrequencyCapEnum = z.enum(FREQUENCY_CAPS);
export const FunnelStatusEnum = z.enum(FUNNEL_STATUSES);
export const FunnelTypeEnum = z.enum(FUNNEL_TYPES);
export const LayoutModeEnum = z.enum(LAYOUT_MODES);
export const LogicOperatorEnum = z.enum(LOGIC_OPERATORS);
export const MatchModeEnum = z.enum(MATCH_MODES);
export const OfferModeEnum = z.enum(OFFER_MODES);
export const OfferSetSelectionModeEnum = z.enum(OFFER_SET_SELECTION_MODES);
export const OperatorTypeEnum = z.enum(OPERATOR_TYPES);
export const PlacementEnum = z.enum(PLACEMENTS);
export const StepTransitionEventEnum = z.enum(STEP_TRANSITION_EVENTS);
export const StepTypeEnum = z.enum(STEP_TYPES);
export const TemplateCategoryEnum = z.enum(TEMPLATE_CATEGORIES);
export const TemplateStatusEnum = z.enum(TEMPLATE_STATUSES);
export const TrafficTypeEnum = z.enum(TRAFFIC_TYPES);
export const TriggerLifecycleEnum = z.enum(TRIGGER_LIFECYCLES);
export const TriggerTypeEnum = z.enum(TRIGGER_TYPES);

export type ActionType = (typeof ACTION_TYPES)[number];
export type BlockType = (typeof BLOCK_TYPES)[number];
export type DiscountType = (typeof DISCOUNT_TYPES)[number];
export type ExperienceType = (typeof EXPERIENCE_TYPES)[number];
export type FlowVariant = (typeof FLOW_VARIANTS)[number];
export type FrequencyCap = (typeof FREQUENCY_CAPS)[number];
export type FunnelStatus = (typeof FUNNEL_STATUSES)[number];
export type FunnelType = (typeof FUNNEL_TYPES)[number];
export type LayoutMode = (typeof LAYOUT_MODES)[number];
export type LogicOperator = (typeof LOGIC_OPERATORS)[number];
export type MatchMode = (typeof MATCH_MODES)[number];
export type OfferMode = (typeof OFFER_MODES)[number];
export type OfferSetSelectionMode =
  (typeof OFFER_SET_SELECTION_MODES)[number];
export type OperatorType = (typeof OPERATOR_TYPES)[number];
export type Placement = (typeof PLACEMENTS)[number];
export type StepTransitionEvent = (typeof STEP_TRANSITION_EVENTS)[number];
export type StepType = (typeof STEP_TYPES)[number];
export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];
export type TemplateStatus = (typeof TEMPLATE_STATUSES)[number];
export type TrafficType = (typeof TRAFFIC_TYPES)[number];
export type TriggerLifecycle = (typeof TRIGGER_LIFECYCLES)[number];
export type TriggerType = (typeof TRIGGER_TYPES)[number];
